import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import PageHeader from "../components/PageHeader";
import { FaFileUpload, FaTrash, FaFileAlt } from "react-icons/fa";
import "./AbrirRecurso.css";

function AbrirRecurso() {
  const { inscricaoId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);

  const [decisao, setDecisao] = useState("");
  const [argumentos, setArgumentos] = useState("");
  const [anexosDescricao, setAnexosDescricao] = useState("");
  const [files, setFiles] = useState([]);

  const MAX_FILES = 15;
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB
  const allowedExt = useMemo(() => new Set(["pdf", "png", "jpg", "jpeg"]), []);
  const isAllowedFile = (f) => {
    const name = String(f?.name || "");
    const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
    const mime = String(f?.type || "").toLowerCase();

    const extOk = allowedExt.has(ext);
    const mimeOk =
      mime === "application/pdf" || mime === "image/png" || mime === "image/jpeg";
    return extOk && mimeOk;
  };

  const fileKey = (f) => `${f.name}:${f.size}:${f.lastModified}`;

  const appendFiles = (picked) => {
    const incoming = Array.isArray(picked) ? picked : [];
    if (!incoming.length) return;

    const invalidType = incoming.filter((f) => !isAllowedFile(f));
    const tooLarge = incoming.filter((f) => Number(f?.size || 0) > MAX_FILE_SIZE);

    if (invalidType.length) {
      addToast("Apenas PDF, PNG ou JPEG (JPG) são permitidos.", "error");
    }
    if (tooLarge.length) {
      addToast("Um dos arquivos excede 15MB (limite por arquivo).", "error");
    }

    const filtered = incoming
      .filter((f) => isAllowedFile(f))
      .filter((f) => Number(f?.size || 0) <= MAX_FILE_SIZE);
    if (!filtered.length) return;

    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const merged = [...prev];
      for (const f of filtered) {
        const key = fileKey(f);
        if (!seen.has(key)) {
          if (merged.length >= MAX_FILES) break;
          merged.push(f);
          seen.add(key);
        }
      }
      if (merged.length >= MAX_FILES && prev.length < merged.length) {
        // ok
      } else if (merged.length >= MAX_FILES && filtered.length) {
        addToast("Limite de 15 arquivos por recurso.", "info");
      }
      return merged;
    });
  };

  const removeFile = (target) => {
    setFiles((prev) => prev.filter((f) => fileKey(f) !== fileKey(target)));
  };

  useEffect(() => {
    let mounted = true;
    async function fetchForm() {
      setLoading(true);
      try {
        const { data } = await api.get(`/recursos/form/inscricao/${inscricaoId}`);
        if (!mounted) return;

        if (data?.existing_recurso_id) {
          addToast(
            "Você já abriu um recurso para esta inscrição. Abrindo o recurso existente...",
            "info"
          );
          navigate(`/recursos/${data.existing_recurso_id}`);
          return;
        }

        setFormData(data);

        // Preenche com placeholders mínimos; usuário edita.
        setDecisao("");
        setArgumentos("");
        setAnexosDescricao("");
      } catch (error) {
        addToast(
          error?.response?.data?.message || "Erro ao carregar dados do recurso.",
          "error"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchForm();
    return () => {
      mounted = false;
    };
  }, [addToast, inscricaoId, navigate]);

  const headerTitle = useMemo(() => {
    if (!formData) return "Abrir Recurso";
    return `Abrir Recurso - Edital Nº ${formData.edital_display}`;
  }, [formData]);

  const canSubmit = useMemo(() => {
    const eligibility = formData?.eligibility;
    if (!eligibility) return true;
    return (
      eligibility.has_nota &&
      eligibility.janela_configurada &&
      eligibility.dentro_da_janela
    );
  }, [formData]);

  const eligibilityMessage = useMemo(() => {
    const eligibility = formData?.eligibility;
    if (!eligibility) return null;

    if (!eligibility.has_nota) {
      return "Recurso disponível somente após a divulgação da nota.";
    }
    if (!eligibility.janela_configurada) {
      return "Período de recurso ainda não configurado para este edital.";
    }
    if (!eligibility.dentro_da_janela) {
      return "Fora do período de envio de recursos para este edital.";
    }
    return null;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData) return;

    setSubmitting(true);
    try {
      const { data: created } = await api.post("/recursos", {
        inscricao_id: formData.inscricao_id,
        decisao_contestada: decisao,
        argumentos,
        anexos_descricao: anexosDescricao,
      });

      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((f) => fd.append("anexos", f));
        await api.post(`/recursos/${created.id}/anexos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      addToast("Recurso enviado com sucesso.", "success");
      navigate("/meus-recursos");
    } catch (error) {
      const status = error?.response?.status;
      const recursoId = error?.response?.data?.recurso_id;
      if (status === 409 && recursoId) {
        addToast(
          "Já existe um recurso para esta inscrição. Abrindo o recurso existente...",
          "info"
        );
        navigate(`/recursos/${recursoId}`);
        return;
      }
      addToast(
        error?.response?.data?.message || "Erro ao enviar recurso.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title={headerTitle} />
        <p>Carregando...</p>
      </>
    );
  }

  if (!formData) {
    return (
      <>
        <PageHeader title="Abrir Recurso" />
        <p>Não foi possível carregar os dados desta inscrição.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader title={headerTitle} />
      <div className="recurso-container">
        <div className="recurso-modelo surface-section">
          <h2>{formData.titulo_modelo}</h2>
          <p className="muted">
            (Modelo base preenchido automaticamente; revise antes de enviar.)
          </p>

          <div className="recurso-grid">
            <div>
              <strong>Nome:</strong> {formData.nome} {formData.sobrenome}
            </div>
            <div>
              <strong>RG:</strong> {formData.rg || "-"}
            </div>
            <div>
              <strong>CPF:</strong> {formData.cpf}
            </div>
            <div>
              <strong>Nº de inscrição:</strong> {String(formData.inscricao_id).padStart(4, "0")}
            </div>
            <div>
              <strong>Cargo:</strong> {formData.nome_cargo}
            </div>
            <div>
              <strong>Processo:</strong> {formData.edital_titulo}
            </div>
          </div>
        </div>

        <form className="form-modern" onSubmit={handleSubmit}>
          {eligibilityMessage ? (
            <div className="surface-section" style={{ padding: 12 }}>
              <strong>Recurso indisponível:</strong> {eligibilityMessage}
            </div>
          ) : null}

          <label>
            Decisão objeto de contestação
            <textarea
              value={decisao}
              onChange={(e) => setDecisao(e.target.value)}
              rows={4}
              placeholder="Explique qual decisão você está contestando (ex: pontuação atribuída, indeferimento, etc.)"
              required
              disabled={!canSubmit || submitting}
            />
          </label>

          <label>
            Argumentos do recurso
            <textarea
              value={argumentos}
              onChange={(e) => setArgumentos(e.target.value)}
              rows={6}
              placeholder="Descreva seus argumentos de forma clara e objetiva."
              required
              disabled={!canSubmit || submitting}
            />
          </label>

          <label>
            Descrição dos anexos (opcional)
            <textarea
              value={anexosDescricao}
              onChange={(e) => setAnexosDescricao(e.target.value)}
              rows={3}
              placeholder="Liste quais documentos você está anexando e por quê."
              disabled={!canSubmit || submitting}
            />
          </label>

          <div className="form-group upload-field" style={{ marginTop: 6 }}>
            <label>
              <FaFileAlt />
              <span>Anexos (opcional) — PDF/PNG/JPEG, até 15 arquivos, 15MB cada</span>
            </label>
            <div className="upload-controls">
              <label className="btn-modern btn-file">
                <FaFileUpload size={18} />
                <span>Escolher arquivos</span>
                <input
                  type="file"
                  multiple
                  accept=".pdf,image/png,image/jpeg,.jpg,.jpeg"
                  onChange={(e) => {
                    appendFiles(Array.from(e.target.files || []));
                    // permite selecionar o mesmo arquivo novamente (caso o usuário remova)
                    e.target.value = null;
                  }}
                  disabled={!canSubmit || submitting}
                  className="visually-hidden"
                />
              </label>
              <span className="upload-selected">
                {files.length ? `${files.length} arquivo(s) selecionado(s)` : "Nenhum arquivo selecionado"}
              </span>
            </div>

            {files.length > 0 ? (
              <ul className="attachment-list">
                {files.map((f) => (
                  <li key={fileKey(f)} className="attachment-item">
                    <span className="attachment-name">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(f)}
                      className="ghost-button button-circle"
                      aria-label={`Remover ${f.name}`}
                      disabled={submitting || !canSubmit}
                    >
                      <FaTrash />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="recurso-actions">
            <button
              type="button"
              className="button-secondary"
              onClick={() => navigate("/minhas-inscricoes")}
              disabled={submitting}
            >
              Voltar
            </button>
            <button type="submit" disabled={submitting || !canSubmit}>
              {submitting ? "Enviando..." : "Enviar recurso"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default AbrirRecurso;
